import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

export interface DataTableColumn<Row> {
  key: keyof Row & string
  label: string
  /** Right-aligned 12px mono with tabular figures (caption / meta). */
  numeric?: boolean
  /** Custom cell renderer (e.g. wrap a value in <Delta/>). */
  render?: (row: Row) => ReactNode
  headerClassName?: string
  cellClassName?: string
}

interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[]
  rows: Row[]
  /** Accessible description of the table's contents. */
  caption?: string
  className?: string
  tableClassName?: string
}

/**
 * Data table (`.rtable`) — spec §5. Bordered 12px-radius wrapper that scrolls
 * horizontally in its own container (the page never scrolls sideways, §6);
 * 12px body + 12px/500 muted headers; numeric cells right-aligned mono.
 */
export function DataTable<Row extends Record<string, ReactNode>>({
  columns,
  rows,
  caption,
  className,
  tableClassName,
}: DataTableProps<Row>) {
  return (
    <div
      tabIndex={0}
      role="region"
      aria-label={caption ?? 'Data table'}
      className={cn(
        'rounded-card border-border bg-card text-text overflow-x-auto border',
        className,
      )}
    >
      <table
        className={cn(
          'text-text w-full border-collapse text-[12px] leading-[1.4]',
          tableClassName,
        )}
      >
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={cn(
                  'text-label text-text-3 border-border border-b px-3.5 py-2.5 text-left',
                  col.numeric && 'text-right',
                  col.headerClassName,
                )}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={cn(
                    'text-text border-border border-b px-3.5 py-2 [tr:last-child_&]:border-0',
                    col.numeric &&
                      'text-text-2 tabular text-right font-mono text-[12px] leading-[1.4]',
                    col.cellClassName,
                  )}
                >
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
